import { useState, useEffect, useRef } from 'react'
import { Box, Chip, CircularProgress, Typography, Avatar, Paper, Stack, IconButton } from '@mui/material'
import {
  AddCircleOutline  as CreatedIcon,
  SwapHoriz         as StatusIcon,
  PhoneOutlined     as CallIcon,
  ChatBubbleOutline as NoteIcon,
  EventOutlined     as FollowUpIcon,
  PersonAddOutlined as AssignIcon,
  LabelOutlined     as TagIcon,
  History           as HistoryIcon,
  EditOutlined      as EditIcon,
  FiberManualRecord as DotIcon,
  AccessTime        as ClockIcon,
  AutoAwesome       as SummaryIcon,
  EmailOutlined     as EmailIcon,
  CheckCircleOutline as ConvertedIcon,
  AttachFile        as AttachmentIcon,
  AssignmentOutlined as TaskIcon,
  Cancel            as LostIcon,
  Refresh           as ReopenIcon,
  AttachMoney       as AmountIcon,
  PlayArrow         as PlayIcon,
  Pause             as PauseIcon,
} from '@mui/icons-material'
import { themeColors } from '../../app/layout/theme'
import { callLogsApi } from '../../lib/api/callLogsApi'
import { AudioPlayer, WAVEFORM_BARS } from './AudioPlayer'

// ── Types ────────────────────────────────────────────────────────────────────

export interface AuditUser {
  id: string
  name: string
  email?: string
  avatarUrl?: string | null
}

export interface StatusHistoryEntry {
  id: string
  fromStatus: string | null
  toStatus: string
  changedAt: string
  changedBy: AuditUser
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name: string) {
  if (!name) return '—'
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}

const AVATAR_COLORS = ['#4f46e5', '#0284c7', '#7b1fa2', '#0891b2', '#b45309', '#15803d', '#c62828']
function avatarColor(name: string) {
  if (!name) return AVATAR_COLORS[0]
  let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

function formatActivityDate(iso: string) {
  if (!iso) return ''
  const d = new Date(iso)
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
  const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
  return `${date}  •  ${time}`
}

function formatFollowUpDate(iso: string) {
  if (!iso) return ''
  const d = new Date(iso)
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
  return `${date}, ${time}`
}

// ── Status Config ─────────────────────────────────────────────────────────────

export const STATUS_LABELS = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  QUALIFIED: 'Qualified',
  CONVERTED: 'Converted',
  JUNK: 'Junk',
}

export const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  NEW:       { color: themeColors.info,    bg: themeColors.infoLight    },
  CONTACTED: { color: themeColors.warning, bg: themeColors.warningLight },
  QUALIFIED: { color: themeColors.success, bg: themeColors.successLight },
  CONVERTED: { color: '#7b1fa2',           bg: '#f3e5f5'                },
  JUNK:      { color: themeColors.error,   bg: themeColors.errorLight   },
}

export function StatusBadge({ status, small = false }: { status: string; small?: boolean }) {
  const cfg = STATUS_COLORS[status] ?? { color: '#757575', bg: '#f5f5f5' }
  return (
    <Chip
      label={STATUS_LABELS[status as keyof typeof STATUS_LABELS] ?? status}
      size="small"
      sx={{ height: small ? 18 : 22, fontSize: small ? '10px' : '11px', fontWeight: 700,
        bgcolor: cfg.bg, color: cfg.color, border: 'none' }}
    />
  )
}

// ── Activity Config mapping ───────────────────────────────────────────────────

type ActivityCfg = { icon: React.ReactNode; color: string; bg: string; label: string }

function getCfg(type: string): ActivityCfg {
  const upperType = (type || '').toUpperCase()
  
  if (upperType === 'ATTACHMENTADDED' || upperType === 'ATTACHMENT_ADDED') {
    return { icon: <AttachmentIcon sx={{ fontSize: 16 }} />, color: '#0284c7', bg: '#f0f9ff', label: 'Attachment' }
  }
  if (upperType === 'LEAD_CONVERTED') {
    return { icon: <ConvertedIcon sx={{ fontSize: 16 }} />, color: '#7b1fa2', bg: '#f3e5f5', label: 'Converted' }
  }
  if (upperType === 'LEAD_CREATED' || upperType === 'CONTACT_CREATED' || upperType === 'CREATE' || upperType === 'CREATED') {
    return { icon: <CreatedIcon sx={{ fontSize: 16 }} />, color: '#16a34a', bg: '#f0fdf4', label: 'Created' }
  }
  if (upperType === 'LEAD_UPDATED' || upperType === 'CONTACT_UPDATED' || upperType === 'UPDATE' || upperType === 'UPDATED') {
    return { icon: <EditIcon sx={{ fontSize: 16 }} />, color: '#6b7280', bg: '#f9fafb', label: 'Updated' }
  }
  if (upperType === 'STATUS_CHANGE' || upperType === 'HISTORY') {
    return { icon: <StatusIcon sx={{ fontSize: 16 }} />, color: '#1d4ed8', bg: '#eff6ff', label: 'Status' }
  }
  if (upperType === 'STAGE_CHANGED') {
    return { icon: <StatusIcon sx={{ fontSize: 16 }} />, color: '#0891b2', bg: '#ecfeff', label: 'Stage changed' }
  }
  if (upperType === 'AMOUNT_UPDATED') {
    return { icon: <AmountIcon sx={{ fontSize: 16 }} />, color: '#059669', bg: '#ecfdf5', label: 'Amount updated' }
  }
  if (upperType === 'CALL') {
    return { icon: <CallIcon sx={{ fontSize: 16 }} />, color: '#7b1fa2', bg: '#faf5ff', label: 'Call' }
  }
  if (upperType === 'NOTE' || upperType === 'COMMENT' || upperType === 'NOTEADDED' || upperType === 'NOTE_ADDED') {
    return { icon: <NoteIcon sx={{ fontSize: 16 }} />, color: '#6b7280', bg: '#f3f4f6', label: 'Comment' }
  }
  if (upperType === 'NOTE_UPDATED' || upperType === 'NOTEUPDATED') {
    return { icon: <NoteIcon sx={{ fontSize: 16 }} />, color: '#6b7280', bg: '#f3f4f6', label: 'Comment updated' }
  }
  if (upperType === 'NOTE_DELETED') {
    return { icon: <NoteIcon sx={{ fontSize: 16 }} />, color: '#9ca3af', bg: '#f9fafb', label: 'Comment deleted' }
  }
  if (upperType === 'FOLLOW_UP') {
    return { icon: <FollowUpIcon sx={{ fontSize: 16 }} />, color: '#0284c7', bg: '#f0f9ff', label: 'Follow-up' }
  }
  if (upperType === 'ASSIGNMENT' || upperType === 'OWNER_CHANGED' || upperType === 'REASSIGNED') {
    return { icon: <AssignIcon sx={{ fontSize: 16 }} />, color: '#0070d2', bg: '#eff6ff', label: 'Assigned' }
  }
  if (upperType === 'TAG_UPDATE') {
    return { icon: <TagIcon sx={{ fontSize: 16 }} />, color: '#6b7280', bg: '#f3f4f6', label: 'Tags' }
  }
  if (upperType === 'EMAIL') {
    return { icon: <EmailIcon sx={{ fontSize: 16 }} />, color: '#0284c7', bg: '#f0f9ff', label: 'Email' }
  }
  if (upperType === 'TASK' || upperType === 'TASK_ADDED') {
    return { icon: <TaskIcon sx={{ fontSize: 16 }} />, color: '#6366f1', bg: '#eef2ff', label: 'Task' }
  }
  if (upperType === 'TASK_COMPLETED') {
    return { icon: <TaskIcon sx={{ fontSize: 16 }} />, color: '#16a34a', bg: '#f0fdf4', label: 'Task completed' }
  }
  if (upperType === 'TASK_DELETED') {
    return { icon: <TaskIcon sx={{ fontSize: 16 }} />, color: '#dc2626', bg: '#fef2f2', label: 'Task deleted' }
  }
  if (upperType === 'EVENT') {
    return { icon: <FollowUpIcon sx={{ fontSize: 16 }} />, color: '#0284c7', bg: '#f0f9ff', label: 'Meeting' }
  }
  if (upperType === 'WON') {
    return { icon: <ConvertedIcon sx={{ fontSize: 16 }} />, color: '#16a34a', bg: '#f0fdf4', label: 'Won' }
  }
  if (upperType === 'SALES') {
    return { icon: <ConvertedIcon sx={{ fontSize: 16 }} />, color: '#7c3aed', bg: '#f5f3ff', label: 'Sales' }
  }
  if (upperType === 'LOST') {
    return { icon: <LostIcon sx={{ fontSize: 16 }} />, color: '#dc2626', bg: '#fef2f2', label: 'Lost' }
  }
  if (upperType === 'REOPENED') {
    return { icon: <ReopenIcon sx={{ fontSize: 16 }} />, color: '#d97706', bg: '#fffbeb', label: 'Reopened' }
  }
  
  return { icon: <EditIcon sx={{ fontSize: 16 }} />, color: '#6b7280', bg: '#f9fafb', label: 'Action' }
}

function getActionText(log: any, entityType: 'lead' | 'contact' | 'customer' | 'sales'): { text: string; bold?: string } {
  const desc = (log.description || '').toLowerCase()
  const type = (log.type || log.action || '').toUpperCase()

  // 1. Customer Audit Log Action Text
  if (entityType === 'customer') {
    let label = 'updated'
    if (type === 'CREATE') label = 'created the organisation'
    else if (type === 'DELETE') label = 'deleted the organisation'
    else if (type === 'COMMENT' || type === 'NOTEADDED' || log.action?.includes('COMMENT') || log.changes?.note || log.changes?.content) label = 'added a comment'
    else if (type === 'NOTE_UPDATED' || type === 'NOTEUPDATED' || log.action === 'NoteUpdated') label = 'updated a comment'
    else if (type === 'EMAIL' || log.action?.includes('EMAIL')) label = 'sent the email'
    else if (type === 'CALL' || log.action?.includes('CALL')) {
      label = (desc.includes('inbound') || desc.includes('reached out') || desc.includes('incoming') || desc.includes('missed')) ? 'received a call' : 'has made a call'
    }
    else if (type === 'ATTACHMENTADDED' || type === 'ATTACHMENT_ADDED' || log.action?.includes('ATTACHMENT')) label = 'uploaded an attachment'
    
    return { text: label }
  }

  // 2. Lead Activity Action Text
  if (entityType === 'lead') {
    switch (type) {
      case 'LEAD_CONVERTED': return { text: 'converted the lead' }
      case 'LEAD_CREATED':  return { text: 'created the lead' }
      case 'LEAD_UPDATED':  return { text: 'updated the lead' }
      case 'STATUS_CHANGE': return { text: 'changed the status' }
      case 'CALL': {
        if (desc.includes('inbound') || desc.includes('reached out') || desc.includes('incoming') || desc.includes('missed')) {
          return { text: 'received a call' }
        }
        return { text: 'has made a call' }
      }
      case 'NOTE':         return { text: 'added a comment' }
      case 'NOTE_UPDATED': return { text: 'updated a comment' }
      case 'NOTE_DELETED': return { text: 'deleted a comment' }
      case 'FOLLOW_UP': {
        if (desc.includes('cancelled')) return { text: 'cancelled a follow-up' }
        if (desc.includes('marked as done') || desc.includes('completed')) return { text: 'completed a follow-up' }
        return { text: 'scheduled a follow-up' }
      }
      case 'ASSIGNMENT': return { text: 'assigned the lead' }
      case 'TAG_UPDATE': {
        const match = (log.description || '').match(/(?:Added|Removed) tag\s+"?([^".\n]+)"?/i)
        const tag = match ? match[1].trim() : ''
        if (desc.startsWith('added')) return { text: 'added tag', bold: `"${tag}"` }
        if (desc.startsWith('removed')) return { text: 'removed tag', bold: `"${tag}"` }
        return { text: 'updated tags' }
      }
      case 'TASK': {
        if (desc.includes('created')) {
          const match = (log.description || '').match(/Task\s+"?([^"\n]+)"?\s+created/i)
          const title = match ? match[1].trim() : ''
          return { text: 'created a task', bold: title ? `"${title}"` : '' }
        }
        if (desc.includes('deleted')) {
          const match = (log.description || '').match(/Task deleted:\s*(.+)$/i)
          const title = match ? match[1].trim() : ''
          return { text: 'deleted a task', bold: title ? `"${title}"` : '' }
        }
        if (desc.includes('completed')) {
          const match = (log.description || '').match(/Task completed:\s*(.+)$/i)
          const title = match ? match[1].trim() : ''
          return { text: 'completed a task', bold: title ? `"${title}"` : '' }
        }
        if (desc.includes('reopened')) {
          const match = (log.description || '').match(/Task reopened:\s*(.+)$/i)
          const title = match ? match[1].trim() : ''
          return { text: 'reopened a task', bold: title ? `"${title}"` : '' }
        }
        return { text: 'updated a task' }
      }
      case 'EVENT': {
        if (desc.includes('cancelled')) return { text: 'cancelled an event' }
        if (desc.includes('marked as done') || desc.includes('completed')) return { text: 'completed an event' }
        return { text: 'created an event' }
      }
      case 'EMAIL':     return { text: 'sent an email' }
      default: return { text: 'performed an action' }
    }
  }

  // 3. Contact Activity Action Text
  if (entityType === 'contact') {
    switch (type) {
      case 'CONTACT_CREATED': return { text: 'created the contact' }
      case 'CONTACT_UPDATED': return { text: 'updated the contact' }
      case 'CALL': {
        if (desc.includes('inbound') || desc.includes('reached out') || desc.includes('incoming') || desc.includes('missed')) {
          return { text: 'received a call' }
        }
        return { text: 'has made a call' }
      }
      case 'NOTE':            return { text: 'added a comment' }
      case 'NOTE_UPDATED':    return { text: 'updated a comment' }
      case 'NOTE_DELETED':    return { text: 'deleted a comment' }
      case 'FOLLOW_UP':       return { text: 'scheduled a follow-up' }
      case 'TAG_UPDATE': {
        const match = (log.description || '').match(/(?:Added|Removed) tag\s+"?([^".\n]+)"?/i)
        const tag = match ? match[1].trim() : ''
        if (desc.startsWith('added')) return { text: 'added tag', bold: `"${tag}"` }
        if (desc.startsWith('removed')) return { text: 'removed tag', bold: `"${tag}"` }
        return { text: 'updated tags' }
      }
      case 'EMAIL':           return { text: 'sent an email' }
      default: return { text: 'performed an action' }
    }
  }

  // 4. Sales Activity Action Text
  if (entityType === 'sales') {
    switch (type) {
      case 'CREATED': return { text: 'created the deal' }
      case 'UPDATED': return { text: 'updated the deal' }
      case 'STAGE_CHANGED': return { text: 'changed the stage' }
      case 'AMOUNT_UPDATED': return { text: 'updated the deal amount' }
      case 'WON': return { text: 'marked the deal as won' }
      case 'SALES': return { text: 'moved the deal to sales' }
      case 'LOST': return { text: 'marked the deal as lost' }
      case 'REOPENED': return { text: 'reopened the deal' }
      case 'OWNER_CHANGED':
      case 'REASSIGNED': return { text: 'reassigned the deal' }
      case 'NOTE_ADDED':
      case 'NOTE':         return { text: 'added a comment' }
      case 'NOTE_UPDATED': return { text: 'updated a comment' }
      case 'NOTE_DELETED': return { text: 'deleted a comment' }
      case 'TASK_ADDED':
      case 'TASK': return { text: 'created a task' }
      case 'TASK_COMPLETED': return { text: 'completed a task' }
      case 'TASK_DELETED': return { text: 'deleted a task' }
      case 'EVENT': return { text: 'scheduled a meeting' }
      case 'CALL': {
        if (desc.includes('inbound') || desc.includes('reached out') || desc.includes('incoming') || desc.includes('missed')) {
          return { text: 'received a call' }
        }
        return { text: 'has made a call' }
      }
      case 'EMAIL': return { text: 'sent an email' }
      case 'ATTACHMENTADDED':
      case 'ATTACHMENT_ADDED': return { text: 'uploaded an attachment' }
      default: return { text: log.title || 'performed an action' }
    }
  }

  return { text: 'performed an action' }
}

// ── Call description parser ───────────────────────────────────────────────────

function parseCallInfo(desc: string) {
  const durationMatch = desc.match(/\((\d+)s\)/)
  const duration  = durationMatch ? `${durationMatch[1]}s` : null
  const isInbound  = desc.toLowerCase().includes('inbound') || desc.toLowerCase().includes('incoming') || desc.toLowerCase().includes('missed')
  const isOutbound = !isInbound
  const outcomeMatch = desc.match(/:\s*(.+)$/)
  const outcome    = outcomeMatch ? outcomeMatch[1].replace(/\.$/, '').trim() : null
  const hasOutcome = !!outcome && !desc.toLowerCase().includes('awaiting outcome')
  const hasSummary = desc.toLowerCase().includes('summary') || desc.length > 80
  return { duration, isOutbound, isInbound, outcome, hasOutcome, hasSummary }
}

// ── Call activity grouping ────────────────────────────────────────────────────

export interface MergedCall {
  representative: any
  duration:       string | null
  callType:       'outbound' | 'inbound'
  summaryDesc:    string | null
}

export function groupActivities(logs: any[]): Array<any | MergedCall> {
  const result: Array<any | MergedCall> = []
  let i = 0

  while (i < logs.length) {
    const log = logs[i]
    const type = (log.type || log.action || '').toUpperCase()

    if (type === 'CALL') {
      const group: any[] = [log]
      let j = i + 1
      while (j < logs.length) {
        const next = logs[j]
        const nextType = (next.type || next.action || '').toUpperCase()
        const diffMs = Math.abs(new Date(next.createdAt).getTime() - new Date(log.createdAt).getTime())
        
        const logCallId = log.metadata?.callLogId
        const nextCallId = next.metadata?.callLogId
        const isSameCall = logCallId && nextCallId && logCallId === nextCallId
        
        if (nextType === 'CALL' && next.performedBy.id === log.performedBy.id && isSameCall && diffMs < 5 * 60_000) {
          group.push(next)
          j++
        } else break
      }

      if (group.length > 1) {
        let duration:    string | null = null
        let summaryDesc: string | null = null
        let callType: 'outbound' | 'inbound' = 'outbound'

        for (const g of group) {
          const d = g.description || ''
          const dl = d.toLowerCase()
          const m = d.match(/\((\d+)s\)/)
          if (m) duration = `${m[1]}s`
          if (dl.includes('inbound')) callType = 'inbound'
          
          if (!dl.includes('call started') && !dl.includes('awaiting outcome') && !dl.includes('call ended')) {
            summaryDesc = d
          } else if (!summaryDesc && !dl.includes('call started')) {
            summaryDesc = d
          }
        }
        if (!summaryDesc) {
          const best = group.find(g => !(g.description || '').toLowerCase().includes('call started'))
          summaryDesc = best?.description ?? null
        }

        result.push({ representative: log, duration, callType, summaryDesc })
        i = j
      } else {
        result.push(log)
        i++
      }
    } else {
      result.push(log)
      i++
    }
  }

  return result
}

// ── Display Card Layout wrappers ──────────────────────────────────────────────

function ContentCard({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ mt: 1.25, border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden', bgcolor: '#fff' }}>
      {children}
    </Box>
  )
}

function CallCard({ log, merged }: { log: any; merged?: MergedCall }) {
  const desc = log.description || log.changes?.note || ''
  const info = parseCallInfo(desc)
  const duration = merged ? merged.duration : info.duration
  const callType = merged ? merged.callType : (info.isInbound ? 'inbound' : 'outbound')
  const summaryDesc = merged ? merged.summaryDesc : desc

  // Pull recordingUrl from metadata (set by SaveCallOutcomeCommand / webhook)
  const metaRecordingUrl: string | null = log.metadata?.recordingUrl ?? null
  const callLogId: string | null = log.metadata?.callLogId ?? null

  // If metadata doesn't carry the recording URL (timing gap / older records),
  // lazily fetch it from the CallLog itself using the callLogId.
  const [resolvedRecordingUrl, setResolvedRecordingUrl] = useState<string | null>(metaRecordingUrl)
  useEffect(() => {
    if (metaRecordingUrl) { setResolvedRecordingUrl(metaRecordingUrl); return }
    if (!callLogId) return
    callLogsApi.getCallLogById(callLogId)
      .then(call => { if (call.recordingUrl) setResolvedRecordingUrl(call.recordingUrl) })
      .catch(() => {})
  }, [metaRecordingUrl, callLogId])

  // Parse durationSeconds for the audio player total time
  const durationSec: number | undefined = (() => {
    if (log.metadata?.durationSeconds) return Number(log.metadata.durationSeconds)
    const m = desc.match(/\((\d+)s\)/)
    if (m) return Number(m[1])
    return undefined
  })()

  const callLabel = callType === 'inbound' ? 'Inbound call' : 'Outbound call'
  const showPlayer = !!duration

  return (
    <ContentCard>
      {showPlayer ? (
        <Box sx={{ p: 1.75, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            <Typography fontSize="13px" fontWeight={600} color="text.primary">{callLabel}</Typography>
            <DotIcon sx={{ fontSize: 6, color: '#9ca3af' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ClockIcon sx={{ fontSize: 13, color: '#9ca3af' }} />
              <Typography fontSize="12.5px" color="text.secondary">{duration}</Typography>
            </Box>
          </Box>
          {resolvedRecordingUrl ? (
            <AudioPlayer recordingUrl={resolvedRecordingUrl} durationSeconds={durationSec} />
          ) : null}
        </Box>
      ) : null}

      {summaryDesc && (
        <Box sx={{ p: 1.75, bgcolor: showPlayer ? '#fafafa' : 'transparent' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <SummaryIcon sx={{ fontSize: 14, color: '#818cf8', flexShrink: 0, mt: '1px' }} />
            <Box>
              <Typography fontSize="12px" fontWeight={700} color="text.primary" mb={0.4}>
                Call Summary
              </Typography>
              <Typography fontSize="12.5px" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {info.hasOutcome ? info.outcome : summaryDesc}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </ContentCard>
  )
}

function StatusChangeCard({ log }: { log: any }) {
  const meta = log.metadata as Record<string, unknown> | null
  if (!meta?.from && !meta?.to) {
    return <ContentCard><Box sx={{ p: 1.75 }}><Typography fontSize="13px" sx={{ lineHeight: 1.6 }}>{log.description}</Typography></Box></ContentCard>
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
      <StatusBadge status={meta.from as string} small />
      <Typography fontSize="13px" color={themeColors.primary} fontWeight={700}>→</Typography>
      <StatusBadge status={meta.to as string} small />
    </Box>
  )
}

// ── Single Timeline Row Component ─────────────────────────────────────────────

interface ActivityRowProps {
  item: any | MergedCall
  isLast: boolean
  entityType: 'lead' | 'contact' | 'customer' | 'sales'
}

function ActivityRow({ item, isLast, entityType }: ActivityRowProps) {
  const isMerged = 'representative' in item
  const log = isMerged ? item.representative : item
  
  const type = (log.type || log.action || '').toUpperCase()
  const cfg = getCfg(type)
  const action = getActionText(log, entityType)
  const color = avatarColor(log.performedBy.name)

  const renderContent = () => {
    if (isMerged) {
      return <CallCard log={log} merged={item} />
    }

    switch (type) {
      case 'NOTE':
      case 'COMMENT':
      case 'NOTEADDED':
      case 'NOTE_ADDED':
        const noteText = log.description || log.changes?.note || log.changes?.content || ''
        return (
          <ContentCard>
            <Box sx={{ p: 1.75 }}>
              <Typography fontSize="13px" color="text.primary" sx={{ lineHeight: 1.65 }}>{noteText}</Typography>
            </Box>
          </ContentCard>
        )
      case 'NOTE_UPDATED':
      case 'NOTEUPDATED':
        const updatedNoteText = log.description || log.changes?.note || log.changes?.content || ''
        return (
          <ContentCard>
            <Box sx={{ p: 1.75, bgcolor: '#fffbeb', borderLeft: '3px solid #f59e0b' }}>
              <Typography fontSize="13px" color="text.primary" sx={{ lineHeight: 1.65 }}>{updatedNoteText}</Typography>
            </Box>
          </ContentCard>
        )
      case 'NOTE_DELETED':
        const deletedNoteText = log.description || log.changes?.note || log.changes?.content || ''
        return (
          <ContentCard>
            <Box sx={{ p: 1.75, bgcolor: '#fafafa' }}>
              <Typography fontSize="13px" color="text.secondary" sx={{ lineHeight: 1.65, fontStyle: 'italic' }}>
                {deletedNoteText}
              </Typography>
            </Box>
          </ContentCard>
        )
      case 'ATTACHMENTADDED':
      case 'ATTACHMENT_ADDED':
        const fileVal = log.description ? (log.description.includes(':') ? log.description.split(':').slice(1).join(':').replace(/"/g, '').trim() : log.description.replace('Attachment added: ', '').replace(/"/g, '')) : (log.changes?.fileName || log.changes?.file || '')
        return fileVal ? (
          <Typography fontSize="12.5px" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
            File: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{fileVal}</Box>
          </Typography>
        ) : null
      case 'CALL':
        return <CallCard log={log} />
      case 'STATUS_CHANGE':
        return <StatusChangeCard log={log} />
      case 'FOLLOW_UP':
        let followUpDesc = log.description
        if (log.metadata?.scheduledAt) {
          const scheduledAtStr = log.metadata.scheduledAt as string
          const localizedTime = formatFollowUpDate(scheduledAtStr)
          const index = followUpDesc.indexOf(' scheduled for ')
          if (index !== -1) {
            followUpDesc = followUpDesc.substring(0, index) + ' scheduled for ' + localizedTime + '.'
          }
        }
        return (
          <ContentCard>
            <Box sx={{ p: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <FollowUpIcon sx={{ fontSize: 15, color: '#0284c7', flexShrink: 0, mt: '1px' }} />
              <Typography fontSize="12.5px" color="text.primary" sx={{ lineHeight: 1.6 }}>{followUpDesc}</Typography>
            </Box>
          </ContentCard>
        )
      case 'EMAIL':
        const emailDesc = log.description || log.changes?.note || ''
        return (
          <ContentCard>
            <Box sx={{ p: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <EmailIcon sx={{ fontSize: 16, color: '#0284c7', mt: '2px' }} />
              <Box>
                <Typography fontSize="13px" fontWeight={600} color="text.primary" mb={0.5}>Email Sent</Typography>
                <Typography fontSize="12.5px" color="text.secondary" sx={{ lineHeight: 1.6 }}>{emailDesc}</Typography>
              </Box>
            </Box>
          </ContentCard>
        )
      case 'TASK':
        if (log.description?.toLowerCase().includes('deleted')) return null;
        let taskMeta = { description: '', status: '', priority: '', dueDate: '' }
        let isJson = false
        try {
          if (log.description?.startsWith('{')) {
            taskMeta = JSON.parse(log.description)
            isJson = true
          } else {
            taskMeta.description = log.description ?? ''
          }
        } catch (e) {
          taskMeta.description = log.description ?? ''
        }
        return (
          <ContentCard>
            <Box sx={{ p: 1.75 }}>
              <Typography fontSize="13px" fontWeight={600} color="text.primary" mb={0.5}>
                {log.title || 'Task Details'}
              </Typography>
              {taskMeta.description && (
                <Typography fontSize="12.5px" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {taskMeta.description}
                </Typography>
              )}
              {isJson && (
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {taskMeta.status && <Chip label={`Status: ${taskMeta.status}`} size="small" sx={{ fontSize: '10px', height: 18 }} />}
                  {taskMeta.priority && <Chip label={`Priority: ${taskMeta.priority}`} size="small" sx={{ fontSize: '10px', height: 18 }} />}
                  {taskMeta.dueDate && <Chip label={`Due: ${taskMeta.dueDate}`} size="small" sx={{ fontSize: '10px', height: 18 }} />}
                </Stack>
              )}
            </Box>
          </ContentCard>
        )
      case 'EVENT':
        return (
          <ContentCard>
            <Box sx={{ p: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <FollowUpIcon sx={{ fontSize: 16, color: '#0284c7', mt: '2px' }} />
              <Box>
                <Typography fontSize="13px" fontWeight={600} color="text.primary" mb={0.5}>
                  {log.title || 'Scheduled Event'}
                </Typography>
                {log.description && (
                  <Typography fontSize="12.5px" color="text.secondary" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {log.description}
                  </Typography>
                )}
              </Box>
            </Box>
          </ContentCard>
        )
      case 'WON':
        return (
          <ContentCard>
            <Box sx={{ p: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1.5, bgcolor: '#f0fdf4' }}>
              <ConvertedIcon sx={{ fontSize: 16, color: '#16a34a', mt: '2px' }} />
              <Box>
                <Typography fontSize="13px" fontWeight={700} color="#166534" mb={0.5}>
                  {log.title || 'Deal Won!'}
                </Typography>
                {log.description && (
                  <Typography fontSize="12.5px" color="#15803d" sx={{ lineHeight: 1.6 }}>
                    {log.description}
                  </Typography>
                )}
              </Box>
            </Box>
          </ContentCard>
        )
      case 'SALES':
        return (
          <ContentCard>
            <Box sx={{ p: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1.5, bgcolor: '#f5f3ff' }}>
              <ConvertedIcon sx={{ fontSize: 16, color: '#7c3aed', mt: '2px' }} />
              <Box>
                <Typography fontSize="13px" fontWeight={700} color="#5c35d8" mb={0.5}>
                  {log.title || 'Moved to Sales'}
                </Typography>
                {log.description && (
                  <Typography fontSize="12.5px" color="#6d28d9" sx={{ lineHeight: 1.6 }}>
                    {log.description}
                  </Typography>
                )}
              </Box>
            </Box>
          </ContentCard>
        )
      case 'LOST':
        return (
          <ContentCard>
            <Box sx={{ p: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1.5, bgcolor: '#fef2f2' }}>
              <LostIcon sx={{ fontSize: 16, color: '#dc2626', mt: '2px' }} />
              <Box>
                <Typography fontSize="13px" fontWeight={700} color="#991b1b" mb={0.5}>
                  {log.title || 'Deal Lost'}
                </Typography>
                {log.description && (
                  <Typography fontSize="12.5px" color="#b91c1c" sx={{ lineHeight: 1.6 }}>
                    {log.description}
                  </Typography>
                )}
              </Box>
            </Box>
          </ContentCard>
        )
      case 'HISTORY':
      case 'UPDATE':
        // If it is standard Customer Audit History
        if (entityType === 'customer' && log.changes?.field) {
          return (
            <Typography fontSize="12.5px" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
              Changed <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{log.changes.field}</Box> from <Box component="span" sx={{ fontStyle: 'italic' }}>{(log.changes.from as string) || '—'}</Box> to <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{(log.changes.to as string) || '—'}</Box>
            </Typography>
          )
        }
        return log.description ? (
          <Typography fontSize="12.5px" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {log.description}
          </Typography>
        ) : null
      case 'LEAD_CONVERTED':
      case 'LEAD_CREATED':
      case 'LEAD_UPDATED':
      case 'CONTACT_CREATED':
      case 'CONTACT_UPDATED':
      case 'CREATE':
      case 'ASSIGNMENT':
      case 'UPDATED':
        if ((type === 'CONTACT_UPDATED' || type === 'UPDATED' || type === 'LEAD_UPDATED' || type === 'REQUIREMENTS_UPDATED') && log.metadata?.field) {
          const hasFromTo = log.metadata.from != null || log.metadata.to != null
          if (hasFromTo) {
            return (
              <Typography fontSize="12.5px" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                Changed <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{log.metadata.field}</Box> from <Box component="span" sx={{ fontStyle: 'italic' }}>{(log.metadata.from as string) || '—'}</Box> to <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{(log.metadata.to as string) || '—'}</Box>
              </Typography>
            )
          }
          // Old records: no from/to stored — fall through to description text
        }
        return log.description ? (
          <Typography fontSize="12.5px" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {log.description}
          </Typography>
        ) : null
      case 'TAG_UPDATE':
        return null // embedded in action title
      default:
        return log.description ? (
          <Typography fontSize="12.5px" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {log.description}
          </Typography>
        ) : null
    }
  }

  return (
    <Box sx={{ display: 'flex', gap: 0, mb: isLast ? 0 : 0.5 }}>
      {/* Spine Left */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 52, pt: '2px' }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '50%',
          bgcolor: cfg.bg, border: `1.5px solid ${cfg.color}28`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: cfg.color, flexShrink: 0, zIndex: 1
        }}>
          {cfg.icon}
        </Box>
        {!isLast && <Box sx={{ width: '1.5px', flex: 1, bgcolor: '#e5e7eb', my: '4px', minHeight: 20 }} />}
      </Box>

      {/* Content Right */}
      <Box sx={{ flex: 1, pb: isLast ? 0 : 3, minWidth: 0, pt: '6px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.1 }}>
          <Avatar sx={{ width: 24, height: 24, fontSize: '10px', fontWeight: 700, bgcolor: color, color: '#fff', flexShrink: 0 }}>
            {getInitials(log.performedBy.name)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6, flexWrap: 'wrap' }}>
              <Typography fontSize="13px" fontWeight={700} color="text.primary">
                {log.performedBy.name}
              </Typography>
              <Typography fontSize="12.5px" color="text.secondary">
                {action.text}
                {action.bold && <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}> {action.bold}</Box>}
              </Typography>
            </Box>
          </Box>
          <Typography fontSize="11.5px" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: 'nowrap', ml: 1, mt: '1px' }}>
            {formatActivityDate(log.createdAt)}
          </Typography>
        </Box>

        {renderContent()}
      </Box>
    </Box>
  )
}

// ── Exported Timeline Components ───────────────────────────────────────────────

interface UniversalActivityTimelineProps {
  logs: any[]
  isLoading?: boolean
  entityType: 'lead' | 'contact' | 'customer' | 'sales'
}

export function normalizeActivities(logs: any[]): any[] {
  return Array.isArray(logs) ? logs.map(log => {
    let parsedLog = { ...log }

    // Normalize type/action
    if (!parsedLog.type && parsedLog.activityType) {
      parsedLog.type = parsedLog.activityType
    }
    if (!parsedLog.type && parsedLog.action) {
      parsedLog.type = parsedLog.action
    }

    // Normalize performedBy
    if (!parsedLog.performedBy) {
      if (parsedLog.createdBy) {
        parsedLog.performedBy = parsedLog.createdBy
      } else if (parsedLog.performedByName) {
        parsedLog.performedBy = {
          id: parsedLog.performedByUserId || '',
          name: parsedLog.performedByName,
          avatarUrl: null,
          email: ''
        }
      } else {
        parsedLog.performedBy = { id: "", name: "System", email: "", avatarUrl: null }
      }
    } else if (typeof parsedLog.performedBy === 'string') {
      parsedLog.performedBy = {
        id: parsedLog.performedBy,
        name: parsedLog.performedByName || 'System',
        avatarUrl: null,
        email: ''
      }
    }

    // Ensure performedBy has a valid name string
    if (parsedLog.performedBy && typeof parsedLog.performedBy === 'object') {
      if (!parsedLog.performedBy.name) {
        parsedLog.performedBy.name = 'System'
      }
    }

    if (log.changesJson && !log.changes) {
      try {
        parsedLog.changes = JSON.parse(log.changesJson)
      } catch (e) {
        // ignore
      }
    }
    if (log.metadataJson && !log.metadata) {
      try {
        parsedLog.metadata = JSON.parse(log.metadataJson)
      } catch (e) {
        // ignore
      }
    }
    if (typeof parsedLog.metadata === 'string') {
      try {
        parsedLog.metadata = JSON.parse(parsedLog.metadata)
      } catch (e) {
        // ignore
      }
    }
    return parsedLog
  }) : []
}

export function UniversalActivityTimeline({ logs, isLoading, entityType }: UniversalActivityTimelineProps) {
  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
  }

  const safeLogs = normalizeActivities(logs)

  if (safeLogs.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, border: '1px dashed #e5e7eb', borderRadius: '12px' }}>
        <HistoryIcon sx={{ fontSize: 44, color: 'rgba(0,0,0,0.1)', mb: 1.5, display: 'block', mx: 'auto' }} />
        <Typography fontSize="14px" fontWeight={600} color="text.secondary">No activity yet</Typography>
        <Typography fontSize="12.5px" color="text.disabled" mt={0.5}>
          Actions will appear here once changes are made
        </Typography>
      </Box>
    )
  }

  const grouped = groupActivities(safeLogs)

  return (
    <Box sx={{ px: 1 }}>
      {grouped.map((item, idx) => (
        <ActivityRow
          key={ 'representative' in item ? item.representative.id : item.id }
          item={item}
          isLast={idx === grouped.length - 1}
          entityType={entityType}
        />
      ))}
    </Box>
  )
}

// ── Status History Timeline (For Leads) ────────────────────────────────────────

function HistoryEntry({ entry, isLast }: { entry: StatusHistoryEntry; isLast: boolean }) {
  const color = avatarColor(entry.changedBy.name)
  return (
    <Box sx={{ display: 'flex', gap: 0, mb: isLast ? 0 : 0.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 52, pt: '2px' }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#eff6ff', border: '1.5px solid #1d4ed828',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1d4ed8', flexShrink: 0, zIndex: 1 }}>
          <HistoryIcon sx={{ fontSize: 16 }} />
        </Box>
        {!isLast && <Box sx={{ width: '1.5px', flex: 1, bgcolor: '#e5e7eb', my: '4px', minHeight: 20 }} />}
      </Box>

      <Box sx={{ flex: 1, pb: isLast ? 0 : 3, minWidth: 0, pt: '6px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '10px', fontWeight: 700, color: '#fff', flexShrink: 0 }}>
            {getInitials(entry.changedBy.name)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6, flexWrap: 'wrap' }}>
              <Typography fontSize="13px" fontWeight={700} color="text.primary">{entry.changedBy.name}</Typography>
              <Typography fontSize="12.5px" color="text.secondary">changed the status</Typography>
            </Box>
          </Box>
          <Typography fontSize="11.5px" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: 'nowrap', ml: 1, mt: '1px' }}>
            {formatActivityDate(entry.changedAt)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          {entry.fromStatus ? (
            <StatusBadge status={entry.fromStatus} small />
          ) : (
            <Typography fontSize="11px" color="text.secondary" fontStyle="italic">—</Typography>
          )}
          <Typography fontSize="13px" color={themeColors.primary} fontWeight={700}>→</Typography>
          <StatusBadge status={entry.toStatus} small />
        </Box>
      </Box>
    </Box>
  )
}

export function StatusHistoryTimeline({ entries, isLoading }: { entries: StatusHistoryEntry[]; isLoading: boolean }) {
  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
  }
  if (entries.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <HistoryIcon sx={{ fontSize: 40, color: 'rgba(0,0,0,0.1)', mb: 1.5, display: 'block', mx: 'auto' }} />
        <Typography fontSize="13px" fontWeight={600} color="text.secondary">No status changes yet</Typography>
      </Box>
    )
  }
  return (
    <Box>
      {entries.map((entry, idx) => (
        <HistoryEntry key={entry.id} entry={entry} isLast={idx === entries.length - 1} />
      ))}
    </Box>
  )
}
